import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  fetchSubsDetail,
  updateSubsServices,
  fetchAvailableServices,
  toggleService,
  updatePrimaryContact,
  setContactModalOpen,
  fetchSubsDocuments,
  uploadSubsDocument,
  deleteSubsDocument,
  setUploadDocumentModalOpen,
  setDocumentsPage,
  setDocumentsPageSize,
  updateSubsDocumentExpiresAt,
} from "./subs-detail.slice";
import { uuid } from "@/interfaces";
import { toast } from "sonner";

export function useSubsDetail(subsId: uuid) {
  const dispatch = useAppDispatch();
  const {
    subsDetail,
    loading,
    updating,
    availableServices,
    servicesLoading,
    selectedServices,
    hasChanges,
    primaryContact,
    contactModalOpen,
    contactSaving,
    documents,
    documentsLoading,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    deletingDocumentId,
    updatingDocumentId,
  } = useAppSelector((s) => s.subsDetail);

  useEffect(() => {
    if (subsId) {
      dispatch(fetchSubsDetail(subsId));
      dispatch(fetchAvailableServices());
    }
  }, [dispatch, subsId]);

  useEffect(() => {
    if (subsId) {
      dispatch(fetchSubsDocuments({ subsId, page: documentsPage, pageSize: documentsPageSize }));
    }
  }, [dispatch, subsId, documentsPage, documentsPageSize]);

  const handleUpdateServices = async (services: uuid[]) => {
    if (!subsId) return;

    try {
      await dispatch(updateSubsServices({ subsId, services })).unwrap();
      toast.success("Services updated successfully");
    } catch (error) {
      toast.error("Failed to update services");
    }
  };

  const handleToggleService = (serviceId: uuid) => {
    dispatch(toggleService(serviceId));
  };

  const handleSaveServices = async () => {
    if (!subsId) return;

    try {
      await dispatch(updateSubsServices({ subsId, services: selectedServices })).unwrap();
      toast.success("Services updated successfully");
    } catch (error) {
      toast.error("Failed to update services");
    }
  };

  const handleOpenContactModal = () => {
    dispatch(setContactModalOpen(true));
  };

  const handleCloseContactModal = () => {
    dispatch(setContactModalOpen(false));
  };

  const handleSaveContact = async (contact: { name: string; email: string; phone: string }) => {
    if (!subsId) return;

    try {
      await dispatch(updatePrimaryContact({ subsId, contact })).unwrap();
      toast.success("Primary contact updated successfully");
    } catch (error) {
      toast.error("Failed to update primary contact");
    }
  };

  const handleUploadDocument = async (file: File, expiresAt?: string | Date) => {
    if (!subsId) return;

    try {
      await dispatch(uploadSubsDocument({ subsId, file, expiresAt })).unwrap();
      toast.success("Document uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload document");
    }
  };

  const handleDeleteDocument = async (docId: uuid) => {
    try {
      await dispatch(deleteSubsDocument(docId)).unwrap();
      toast.success("Document deleted successfully");
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleUpdateDocumentExpiresAt = async (docId: uuid, expiresAt: string | Date | null) => {
    try {
      await dispatch(updateSubsDocumentExpiresAt({ docId, expiresAt })).unwrap();
      toast.success("Document expiration updated successfully");
    } catch (error) {
      toast.error("Failed to update document expiration");
      throw error;
    }
  };

  return {
    subsDetail,
    loading,
    updating,
    updateSubsServices: handleUpdateServices,
    availableServices,
    servicesLoading,
    selectedServices,
    hasChanges,
    handleToggleService,
    handleSaveServices,
    primaryContact,
    contactModalOpen,
    contactSaving,
    documents,
    documentsLoading,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    deletingDocumentId,
    updatingDocumentId,
    handleOpenContactModal,
    handleCloseContactModal,
    handleSaveContact,
    setUploadDocumentModalOpen: (open: boolean) => dispatch(setUploadDocumentModalOpen(open)),
    setDocumentsPage: (page: number) => dispatch(setDocumentsPage(page)),
    setDocumentsPageSize: (pageSize: number) => dispatch(setDocumentsPageSize(pageSize)),
    uploadDocument: handleUploadDocument,
    deleteDocument: handleDeleteDocument,
    updateDocumentExpiresAt: handleUpdateDocumentExpiresAt,
  };
}
